import React from 'react';
import { motion } from 'framer-motion';
import { Card, Section } from './index';

/**
 * Roadmap section displaying BigCash Coffee Token development milestones
 */
const Roadmap = () => {
  const roadmapItems = [
    {
      phase: 'Phase 1',
      title: 'Token Launch & Rebranding',
      status: 'completed',
      date: 'Q4 2018 - Q1 2024',
      items: [
        'Initial token launch on Ethereum',
        'Transition from e-commerce to coffee focus',
        'Community building and awareness',
        'Website and branding overhaul'
      ],
      icon: '🚀'
    },
    {
      phase: 'Phase 2',
      title: 'Community Rewards System',
      status: 'in-progress',
      date: 'Q2 2024',
      items: [
        'Launch coffee lover rewards program',
        'Partner with local coffee shops',
        'Implement loyalty point system',
        'Mobile app development'
      ],
      icon: '☕'
    },
    {
      phase: 'Phase 3',
      title: 'Strategic Partnerships',
      status: 'upcoming',
      date: 'Q3 2024',
      items: [
        'Major coffee chain partnerships',
        'Integration with coffee delivery apps',
        'Expand to regional markets',
        'Corporate coffee solutions'
      ],
      icon: '🤝'
    },
    {
      phase: 'Phase 4',
      title: 'DeFi Integration & Staking',
      status: 'upcoming',
      date: 'Q4 2024',
      items: [
        'Launch BGC staking platform',
        'Yield farming opportunities',
        'Liquidity mining programs',
        'Cross-chain bridge development'
      ],
      icon: '💎'
    },
    {
      phase: 'Phase 5',
      title: 'Global Expansion',
      status: 'future',
      date: '2025',
      items: [
        'International market expansion',
        'Multi-language platform support',
        'Global coffee festival sponsorships',
        'Coffee NFT marketplace'
      ],
      icon: '🌍'
    }
  ];
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in-progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'upcoming':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'future':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return '✅';
      case 'in-progress':
        return '🔄';
      case 'upcoming':
        return '⏳';
      case 'future':
        return '🔮';
      default:
        return '📅';
    }
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };
  
  return (
    <Section id="roadmap" background="coffee" className="relative">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 text-8xl rotate-12">☕</div>
        <div className="absolute bottom-20 right-10 text-6xl -rotate-12">🫘</div>
      </div>
      
      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
        className="relative z-10"
      >
        {/* Section Header */}
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold font-display mb-6 text-white">
            Development Roadmap
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-cream-300 to-cream-100 mx-auto mb-8"></div>
          <p className="text-lg text-coffee-100 max-w-2xl mx-auto">
            Our journey to revolutionize coffee culture through blockchain technology
          </p>
        </motion.div>
        
        {/* Roadmap Timeline */}
        <div className="max-w-4xl mx-auto">
          {roadmapItems.map((item, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="relative mb-12 last:mb-0"
            >
              {/* Timeline Line */}
              {index < roadmapItems.length - 1 && (
                <div className="absolute left-6 top-20 w-0.5 h-full bg-coffee-300 opacity-30"></div>
              )}
              
              <div className="flex items-start space-x-6">
                {/* Timeline Icon */}
                <div className="flex-shrink-0 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg border-4 border-coffee-200 relative z-10">
                  <span className="text-xl">{item.icon}</span>
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <Card className="hover:shadow-2xl transition-all duration-300">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                      <div>
                        <h3 className="text-2xl font-bold text-coffee-800 mb-2">
                          {item.phase}: {item.title}
                        </h3>
                        <p className="text-coffee-600 font-medium">{item.date}</p>
                      </div>
                      
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border mt-2 md:mt-0 ${getStatusColor(item.status)}`}>
                        <span className="mr-1">{getStatusIcon(item.status)}</span>
                        {item.status.charAt(0).toUpperCase() + item.status.slice(1).replace('-', ' ')}
                      </div>
                    </div>
                    
                    <ul className="space-y-2">
                      {item.items.map((task, taskIndex) => (
                        <li key={taskIndex} className="flex items-start space-x-2">
                          <span className="text-coffee-500 mt-1">•</span>
                          <span className="text-coffee-700">{task}</span>
                        </li>
                      ))}
                    </ul>
                  </Card>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Call to Action */}
        <motion.div variants={itemVariants} className="text-center mt-16">
          <Card variant="gradient" className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-coffee-800 mb-4">
              Join Our Journey
            </h3>
            <p className="text-coffee-600 mb-6">
              Be part of the BigCash Coffee Token community and help shape the future 
              of coffee culture in the blockchain era.
            </p>
            <div className="flex justify-center space-x-4 text-2xl">
              <span>🚀</span>
              <span>☕</span>
              <span>🌟</span>
              <span>🤝</span>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </Section>
  );
};

export default Roadmap;